import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { flexRender, type Table as TsTable } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { cn } from '../../lib/classNameHelpers';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../Accordion/Accordion';
import { Skeleton } from '../Skeleton/Skeleton';

function EmptyState({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-40 items-center justify-center rounded-lg border border-borderSecondary bg-backgroundPrimary p-4">
      <div className="text-center text-xs text-contentSecondary">{children}</div>
    </div>
  );
}

function LoadingSkeleton({ count }: { count: number }) {
  return (
    <div className="space-y-1">
      {Array.from({ length: count }).map((_, index) => (
        <div key={`skeleton-${index}`} className="rounded-lg bg-neutral-800 px-3 py-2">
          <div className="flex items-center justify-between">
            <div className="grid w-full grid-cols-3 gap-6">
              <div className="min-w-0 space-y-1">
                <Skeleton className="h-2.5 w-12" />
                <Skeleton className="h-3 w-16" />
              </div>
              <div className="min-w-0 space-y-1">
                <Skeleton className="h-2.5 w-12" />
                <Skeleton className="h-3 w-16" />
              </div>
              <div className="min-w-0 space-y-1">
                <Skeleton className="h-2.5 w-12" />
                <Skeleton className="h-3 w-16" />
              </div>
            </div>
            <ChevronDownIcon className="ml-2 size-5 shrink-0 text-contentSecondary" />
          </div>
        </div>
      ))}
    </div>
  );
}

type AccordionListProps<T> = {
  table: TsTable<T>;
  isError?: boolean;
  isPending: boolean;
  className?: string;
  noDataText?: string;
  isConnected: boolean;
  isFetching?: boolean;
  hasActions?: boolean;
  skeletonRows?: number;
  requireConnected?: boolean;
  style?: React.CSSProperties;
  onRowClick?: (row: T) => void;
  ref?: React.RefObject<HTMLDivElement | null>;
};

export const AccordionList = <T,>({
  ref,
  table,
  style,
  isError,
  className,
  isPending,
  isFetching,
  noDataText,
  onRowClick,
  isConnected,
  skeletonRows = 4,
  requireConnected = true,
  hasActions: _hasActions = false,
}: AccordionListProps<T>) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const rows = table.getRowModel().rows;

  const headerMap = useMemo(() => {
    const headerGroup = table.getHeaderGroups()[0];
    return new Map(headerGroup?.headers.map((h) => [h.id, h]) || []);
  }, [table]);

  const handleValueChange = useCallback((values: string | string[]) => {
    setExpandedRows(new Set(Array.isArray(values) ? values : []));
  }, []);

  if (!isConnected && requireConnected) {
    return (
      <div ref={ref} className={cn('w-full', className)} style={style}>
        <EmptyState>Connect your wallet to deposit funds & start trading</EmptyState>
      </div>
    );
  }

  if (isPending && isFetching) {
    return (
      <div ref={ref} className={cn('w-full px-2', className)} style={style}>
        <LoadingSkeleton count={skeletonRows} />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div ref={ref} className={cn('w-full', className)} style={style}>
        <EmptyState>
          {isError ? (
            <div className="space-y-1">
              <p className="text-contentTertiary">Failed to fetch table.</p>
              <p className="text-contentSecondary">Try reloading the page.</p>
            </div>
          ) : (
            noDataText
          )}
        </EmptyState>
      </div>
    );
  }

  return (
    <div ref={ref} className={cn('w-full', className)} style={style}>
      <Accordion
        type="multiple"
        collapsible
        value={Array.from(expandedRows)}
        onValueChange={handleValueChange}
      >
        {rows.map((row) => {
          const cells = row.getVisibleCells();
          const visibleCells = cells.filter(
            (cell) => !cell.column.columnDef.meta?.accordion?.hidden
          );

          const triggerCells = visibleCells.filter((cell) => {
            const accordion = cell.column.columnDef.meta?.accordion;
            return accordion?.showInTrigger;
          });

          const contentCells = visibleCells.filter(
            (cell) =>
              !triggerCells.includes(cell) && !cell.column.columnDef.meta?.accordion?.isActionColumn
          );

          const actionCells = visibleCells.filter(
            (cell) => cell.column.columnDef.meta?.accordion?.isActionColumn
          );

          return (
            <AccordionItem
              key={row.id}
              value={row.id}
              className={table.options.meta?.getRowClassName?.(row)}
            >
              <AccordionTrigger
                hideChevron={!!onRowClick}
                className={onRowClick ? 'cursor-pointer' : undefined}
              >
                <div className="grid w-full grid-cols-3 gap-6">
                  {triggerCells.map((cell) => {
                    const header = headerMap.get(cell.column.id);
                    const headerText = header
                      ? flexRender(cell.column.columnDef.header, header.getContext())
                      : cell.column.id;

                    return (
                      <div
                        key={cell.id}
                        className={cn(
                          'min-w-0 flex flex-col',
                          cell.column.columnDef.meta?.cellContainerClassName
                        )}
                      >
                        <span className="text-3xs font-medium text-contentSecondary">
                          {headerText}
                        </span>
                        <span className="text-xs">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4 pt-4">
                  <div className="grid grid-cols-3 gap-6 pr-7 text-xs">
                    {contentCells.map((cell) => {
                      const header = headerMap.get(cell.column.id);
                      const headerText = header
                        ? flexRender(cell.column.columnDef.header, header.getContext())
                        : cell.column.id;
                      const value = flexRender(cell.column.columnDef.cell, cell.getContext());

                      return (
                        <div
                          key={cell.id}
                          className={cn(
                            'flex flex-col',
                            cell.column.columnDef.meta?.cellContainerClassName
                          )}
                        >
                          <span className="text-3xs font-medium text-contentSecondary">
                            {headerText}
                          </span>
                          <span className="text-wrap text-xs">{value}</span>
                        </div>
                      );
                    })}
                  </div>
                  {actionCells.length > 0 && (
                    <div className="flex items-center justify-end gap-4 pt-2">
                      {actionCells.map((cell) => (
                        <div
                          key={cell.id}
                          onClick={(e) => e.stopPropagation()}
                          className={cell.column.columnDef.meta?.cellContainerClassName}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
};
